/**
 * Present Mode entry/exit + playlist wiring for the dashboard workspace tab
 * (S13). Kept out of `DashboardTab.tsx` to keep that file's growth minimal —
 * it already owns the tab shell, add-tile dialog, and alert wiring.
 *
 * Two entry points converge here: the toolbar kebab's "Present" item calls
 * `enterPresent()` directly; the Object Explorer node's "Present" context
 * menu item (`presentDashboardFromExplorer`) opens/focuses the tab and drops
 * a request into `presentRequestStore`, consumed by the effect below once
 * the matching dashboard resolves. Either way, analytics fires exactly once
 * per entry, from one place.
 */
import { useCallback, useEffect, useState } from 'react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { usePresentRequestStore } from '../store/presentRequestStore';
import { usePresentPlaylist, type PresentPlaylistState } from './usePresentPlaylist';
import type { Dashboard } from '../types/dashboardTypes';

export type DashboardPresentState = PresentPlaylistState & {
  presenting: boolean;
  enterPresent: () => void;
  exitPresent: () => void;
};

export function useDashboardPresent(
  dashboard: Dashboard | null,
  dashboards: Dashboard[],
  isTabActive: boolean,
): DashboardPresentState {
  const { track } = useAnalytics();
  const [presenting, setPresenting] = useState(false);
  const requestedDashboardId = usePresentRequestStore((s) => s.requestedDashboardId);
  const consumeRequest = usePresentRequestStore((s) => s.consume);

  const enterPresent = useCallback(() => {
    if (!dashboard) return;
    setPresenting(true);
    track('feature:dashboard_present', {
      enabled: true,
      playlist: Boolean(dashboard.playlist && dashboard.playlist.dashboardIds.length > 0),
    });
  }, [dashboard, track]);

  // Toggle-both-ways (repo analytics rule): only the deliberate "Esc / exit"
  // action calls this — the isTabActive force-exit effect below bypasses it
  // (switching tabs away isn't a user "exit" click, just cleanup).
  const exitPresent = useCallback(() => {
    setPresenting(false);
    track('feature:dashboard_present', { enabled: false });
  }, [track]);

  // Explorer-originated request: fire once the tab is showing the requested
  // dashboard (may take a render or two after `openDashboardTab` focuses it).
  useEffect(() => {
    if (requestedDashboardId && dashboard?.id === requestedDashboardId && !presenting) {
      enterPresent();
      consumeRequest();
    }
  }, [requestedDashboardId, dashboard, presenting, enterPresent, consumeRequest]);

  // Switching workspace tabs away from a presenting dashboard exits — the
  // fullscreen overlay is nested under this tab's DOM and would otherwise
  // stay "on" invisibly (CSS-hidden by the inactive-tab ancestor) and pop
  // back up unexpectedly when the user returns.
  useEffect(() => {
    if (!isTabActive) setPresenting(false);
  }, [isTabActive]);

  const playlist = usePresentPlaylist(dashboard, dashboards, presenting);

  return { presenting, enterPresent, exitPresent, ...playlist };
}
